/* global globalThis */
/**
 * SearchPage Component
 *
 * A comprehensive search interface for members, providers, pharmacies, and programs.
 * Supports filtering, sorting, and quick view functionality for search results.
 *
 * Features:
 * - Unified search across multiple entity types
 * - Advanced filtering with autocomplete
 * - Paginated results with sorting
 * - Quick view for member details
 * - Responsive design for all screen sizes
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.isPriorityOutreach] - Flag indicating if in priority outreach mode
 * @returns {JSX.Element} The rendered search interface
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useLocation, Link } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Download as DownloadIcon, Plus as PlusIcon } from "lucide-react";
import { downloadExcelFile } from "../utils/exportHelper";
import AddPatientModal from "../components/modals/AddPatientModal";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CloseIcon from "@mui/icons-material/Close";
import MemberQuickview from "../components/members/MemberQuickview";
import { styled } from "@mui/material/styles";

import { fetchPriorityOutreach } from "../api/priority";
import { getOutcomesOutreach } from "../api/outcomesOutreach";
import { fetchAssessmentReferenceData } from "../api/assessment";
import { isUserL9Role } from "../utils/role";
import _ from "lodash";

import CommonSearchBar from "../components/common/searchBar/CommonSearchBar";
import CommonFilters from "../components/common/commonFilters/CommonFilters";
import MobileCommonFilters from "../components/common/mobileCommonFilters/CommonFilters";
import CommonTable from "../components/common/table/commonTable";

import PageHeader from "../components/common/PageHeader";

import { checkboxColumn, getColumns } from "../components/common/table/columns";
import ToggleButton from "../components/common/toggleButton/toggleSlider";
import {
  fetchPrograms,
  fetchCarriers,
  fetchGroups,
  fetchAssignees,
  updateBulkMemberAssignee,
} from "../api/filterType";
import CommonSingleSelect from "../components/common/commonFilters/commonSingleSelect";

import "./Searchpage.css";
import { useLoading } from "../context/LoadingContext";
import { useDispatch, useSelector } from "react-redux";
import { togglePhi } from "../redux/slices/togglesSlice";
import {
  setFilters as setMembersFilters,
  resetFilters as resetMembersFilters,
} from "../redux/slices/membersFilterSlice";

const MainContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: open ? "calc(100% - 240px)" : "100%",
  marginLeft: open ? "240px" : "0",
  minHeight: "80vh",
  backgroundColor: "#F7F8FB",
  overflow: "hidden !important",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const MobileFilterWrapper = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    "& .common-filters": {
      display: "flex !important",
      flexDirection: "row !important",
      flexWrap: "nowrap !important",
      overflowX: "auto !important",
      gap: "8px !important",
      paddingBottom: "4px !important",
      alignItems: "center !important",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    "& .common-filter-input": {
      flex: "0 0 auto !important",
      minWidth: "auto !important",
      width: "auto !important",
    },
    "& .filter-label": {
      display: "none !important",
    },
    "& .filter-input-container": {
      height: "36px",
      minWidth: "auto !important",
      width: "fit-content !important",
      maxWidth: "150px",
      backgroundColor: "#fff",
      border: "1px solid #E0E0E0",
      borderRadius: "8px",
    },
    "& .filter-input": {
      fontSize: "13px",
      padding: "6px 8px !important",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
}));

/**
 * Estimates the diagonal screen size in inches based on screen resolution and DPI
 * @returns {number} Diagonal screen size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.window.screen;
  const dpi = globalThis.window.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

/**
 * Configuration object for different search sections
 * @constant
 * @type {Object}
 * @property {Object} members - Members section configuration
 * @property {string} members.description - Description of members section
 * @property {Object} providers - Providers section configuration
 * @property {string} providers.title - Title for providers section
 * @property {string} providers.description - Description of providers section
 * @property {Object} pharmacies - Pharmacies section configuration
 * @property {string} pharmacies.title - Title for pharmacies section
 * @property {string} pharmacies.description - Description of pharmacies section
 * @property {Object} programs - Programs section configuration
 * @property {string} programs.title - Title for programs section
 * @property {string} programs.description - Description of programs section
 */
const SECTIONS = {
  members: {
    description: "Search and manage member information",
  },
  providers: {
    title: "Providers",
    description: "Search and manage provider information",
  },
  pharmacies: {
    title: "Pharmacies",
    description: "Search and manage pharmacy information",
  },
  programs: {
    title: "Programs",
    description: "Search and manage program information",
  },
};

const getSortParams = (sorting) => {
  const firstSort = Array.isArray(sorting) ? sorting[0] : undefined;
  const sortBy = firstSort?.id;
  let sortDirection;
  if (firstSort) {
    sortDirection = firstSort.desc ? "desc" : "asc";
  }
  return { sortBy, sortDirection };
};

const determineOutreachType = (isPriorityOutreach, isMemberMonitoring) => {
  if (isPriorityOutreach) return "MEMBER_OUTREACH";
  if (isMemberMonitoring) return "MONITORING_OUTREACH";
  return "ALL";
};

const getRecentActivityLabel = (item) =>
  item?.displayName ||
  item?.outcomeDisplayName ||
  item?.label ||
  item?.name ||
  item?.title ||
  "";

const getRecentActivityOutcomeCode = (item) =>
  item?.outcomeName ??
  item?.outcome ??
  item?.outcomeCode ??
  item?.outcomeDisplayName ??
  item?.code ??
  item?.id ??
  item?.value ??
  "";

const getRecentActivityReasonCode = (item) =>
  item?.reason ??
  item?.subCategoryCode ??
  item?.reasonCode ??
  item?.displayName ??
  item?.code ??
  item?.id ??
  item?.value ??
  "";

const getRecentActivityChildren = (item) => {
  if (Array.isArray(item?.subReasons)) return item.subReasons;
  if (Array.isArray(item?.reasons)) return item.reasons;
  if (Array.isArray(item?.children)) return item.children;
  return [];
};

const buildRecentActivityLookup = (outcomesList = []) => {
  const outcomeLookup = new Map();
  const reasonLookup = new Map();
  const reasonToOutcomeCodeLookup = new Map();
  const reasonToParentLabelLookup = new Map();

  for (const outcome of Array.isArray(outcomesList) ? outcomesList : []) {
    const outcomeLabel = getRecentActivityLabel(outcome);
    if (outcomeLabel) {
      const primaryCode = getRecentActivityOutcomeCode(outcome) || outcomeLabel;
      const allCodes = outcome.allOutcomeCodes || [primaryCode];
      outcomeLookup.set(outcomeLabel, allCodes);
    }

    for (const reason of getRecentActivityChildren(outcome)) {
      const reasonLabel = getRecentActivityLabel(reason);
      if (reasonLabel) {
        const reasonCode = getRecentActivityReasonCode(reason) || reasonLabel;
        const specificOutcomeCode =
          reason.outcomeCode ||
          getRecentActivityOutcomeCode(outcome) ||
          outcomeLabel;

        reasonLookup.set(reasonLabel, reasonCode);
        reasonToOutcomeCodeLookup.set(reasonLabel, specificOutcomeCode);
        reasonToParentLabelLookup.set(reasonLabel, outcomeLabel);
      }
    }
  }

  return {
    outcomeLookup,
    reasonLookup,
    reasonToOutcomeCodeLookup,
    reasonToParentLabelLookup,
  };
};

const collectReasonsByOutcomeCode = (
  selectedReasonLabels,
  {
    outcomeLookup,
    reasonLookup,
    reasonToOutcomeCodeLookup,
    reasonToParentLabelLookup,
  },
) => {
  const outcomeCodeToReasonsMap = new Map();

  for (const reasonLabel of selectedReasonLabels) {
    const parentLabel = reasonToParentLabelLookup.get(reasonLabel);
    const reasonCode = reasonLookup.get(reasonLabel) || reasonLabel;
    const specificOutcomeCode =
      reasonToOutcomeCodeLookup.get(reasonLabel) ||
      (parentLabel ? outcomeLookup.get(parentLabel)?.[0] : null) ||
      parentLabel;

    if (!specificOutcomeCode || !reasonCode) continue;

    if (!outcomeCodeToReasonsMap.has(specificOutcomeCode)) {
      outcomeCodeToReasonsMap.set(specificOutcomeCode, new Set());
    }
    outcomeCodeToReasonsMap.get(specificOutcomeCode).add(reasonCode);
  }

  return outcomeCodeToReasonsMap;
};

const addEmptyOutcomeEntries = (
  selectedOutcomeLabels,
  outcomeLookup,
  outcomeCodeToReasonsMap,
) => {
  for (const outcomeLabel of selectedOutcomeLabels) {
    const outcomeCodes = outcomeLookup.get(outcomeLabel) || [outcomeLabel];

    const hasAnySubReasonForParent = outcomeCodes.some(
      (code) =>
        outcomeCodeToReasonsMap.has(code) &&
        outcomeCodeToReasonsMap.get(code).size > 0,
    );

    if (hasAnySubReasonForParent) continue;

    for (const code of outcomeCodes) {
      if (!outcomeCodeToReasonsMap.has(code)) {
        outcomeCodeToReasonsMap.set(code, new Set());
      }
    }
  }
};

const mapRecentActivitySelections = (
  recentActivityOutcomeFilter,
  recentActivityReasonFilter,
  recentActivityOutcomesList,
) => {
  const lookups = buildRecentActivityLookup(recentActivityOutcomesList);

  const selectedOutcomeLabels = Array.isArray(recentActivityOutcomeFilter)
    ? recentActivityOutcomeFilter
    : [];
  const selectedReasonLabels = Array.isArray(recentActivityReasonFilter)
    ? recentActivityReasonFilter
    : [];

  const outcomeCodeToReasonsMap = collectReasonsByOutcomeCode(
    selectedReasonLabels,
    lookups,
  );
  addEmptyOutcomeEntries(
    selectedOutcomeLabels,
    lookups.outcomeLookup,
    outcomeCodeToReasonsMap,
  );

  const outcomes = [];
  outcomeCodeToReasonsMap.forEach((reasonsSet, outcomeCode) => {
    outcomes.push({
      outcome: outcomeCode,
      subCategory: Array.from(reasonsSet),
    });
  });

  return {
    outcomes,
  };
};

const normalizeRecentActivityOutcomes = (response) => {
  let rawList = [];
  if (Array.isArray(response?.outcomes)) {
    rawList = response.outcomes;
  } else if (Array.isArray(response?.content)) {
    rawList = response.content;
  } else if (Array.isArray(response)) {
    rawList = response;
  }

  const combined = [];
  const mapByName = {};

  rawList.forEach((item) => {
    const outcomeCode = getRecentActivityOutcomeCode(item);
    const name = getRecentActivityLabel(item);
    const key = String(name).trim().toLowerCase();

    const taggedSubReasons = Array.isArray(item.subReasons)
      ? item.subReasons.map((sr) => ({
          ...sr,
          outcomeCode: sr.outcomeCode || outcomeCode,
        }))
      : [];

    if (!mapByName[key]) {
      const newItem = {
        ...item,
        allOutcomeCodes: [outcomeCode].filter(Boolean),
        subReasons: taggedSubReasons,
      };
      mapByName[key] = newItem;
      combined.push(newItem);
    } else {
      if (
        outcomeCode &&
        !mapByName[key].allOutcomeCodes.includes(outcomeCode)
      ) {
        mapByName[key].allOutcomeCodes.push(outcomeCode);
      }
      if (taggedSubReasons.length > 0) {
        const existingSubReasons = mapByName[key].subReasons || [];
        const existingKeys = new Set(
          existingSubReasons.map((s) => getRecentActivityReasonCode(s)),
        );
        taggedSubReasons.forEach((sr) => {
          const srKey = getRecentActivityReasonCode(sr);
          if (!existingKeys.has(srKey)) {
            existingKeys.add(srKey);
            existingSubReasons.push(sr);
          }
        });
      }
    }
  });

  return combined;
};

const buildFetchParams = ({
  page,
  size,
  search,
  sorting,
  selectedAssigneeIds,
  selectedCarrierIds,
  selectedGroupIds,
  selectedDispositionIds,
  selectedProgramIds,
  recentActivityOutcomeFilter,
  recentActivityReasonFilter,
  recentActivityOutcomesList,
  isPriorityOutreach,
  isMemberMonitoring,
  isL6L7,
  claimType,
}) => {
  const { sortBy: rawSortBy, sortDirection } = getSortParams(sorting);
  const { outcomes } = mapRecentActivitySelections(
    recentActivityOutcomeFilter,
    recentActivityReasonFilter,
    recentActivityOutcomesList,
  );

  const requestBody = {
    assigneeIds: selectedAssigneeIds || [],
    carrierCodes: selectedCarrierIds || [],
    groupNumbers: selectedGroupIds || [],
    memberStatuses: selectedDispositionIds || [],
    programIds: selectedProgramIds || [],
    outcomes,
  };

  const outreachTypeVal = determineOutreachType(
    isPriorityOutreach,
    isMemberMonitoring,
  );

  const sortBy =
    outreachTypeVal === "MEMBER_OUTREACH" && rawSortBy === "latestDateAdded"
      ? "dateAdded"
      : rawSortBy;

  const signature = JSON.stringify({
    page: Number(page),
    size: Number(size),
    search: search.trim(),
    sortBy,
    sortDirection,
    ...(isPriorityOutreach && isL6L7 ? { claim_type: claimType } : {}),
    outreachType: outreachTypeVal,
    body: requestBody,
  });

  const queryParams = {
    page: Number(page),
    size: Number(size),
    search: search.trim(),
    ...(sortBy ? { sortBy } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(isPriorityOutreach && isL6L7 ? { claim_type: claimType } : {}),
    outreachType: outreachTypeVal,
    body: requestBody,
  };

  return { signature, queryParams };
};

const normalizeResponse = (response) => {
  let content = [];
  if (Array.isArray(response?.content)) {
    content = response.content;
  } else if (Array.isArray(response)) {
    content = response;
  }

  let totalElementsValue = 0;
  if (typeof response?.totalElements === "number") {
    totalElementsValue = response.totalElements;
  } else if (Array.isArray(response?.content)) {
    totalElementsValue = response.content.length;
  } else if (Array.isArray(response)) {
    totalElementsValue = response.length;
  }

  return {
    content,
    totalElements: totalElementsValue,
    isLastPage: Boolean(response?.isLastPage),
    excludedCount:
      typeof response?.excludedCount === "number" ? response.excludedCount : 0,
    hasExcludedCount: typeof response?.excludedCount === "number",
    addedCount:
      typeof response?.addedCount === "number" ? response.addedCount : 0,
    rawCount: typeof response?.rawCount === "number" ? response.rawCount : 0,
  };
};

// Extract value inside last parentheses without regex (safe)
const extractLastParenValue = (str) => {
  if (typeof str !== "string") return undefined;
  const close = str.lastIndexOf(")");
  if (close === -1) return undefined;
  const open = str.lastIndexOf("(", close);
  if (open === -1) return undefined;
  return str.slice(open + 1, close);
};

const mergeDropdownOptions = (replace, prev, names) => {
  if (replace) return names;
  const merged = [...prev, ...names];
  const seen = new Set();
  return merged.filter((n) =>
    n && !seen.has(n) ? (seen.add(n), true) : false,
  );
};

const processProgramOptions = async (ot, search, claimType) => {
  const params = { outreachType: ot };
  if (claimType) params.claim_type = claimType;
  if (typeof search === "string" && search.trim() !== "") {
    params.search = search.trim();
  }
  const data = await fetchPrograms(params);
  const map = {};
  const names = [];
  for (const item of data) {
    if (item.programName && item.programId) {
      map[item.programName] = item.programId;
      names.push(item.programName);
    }
  }
  return { map, names };
};

const parseCarrierData = (data) => {
  const map = {};
  const names = [];
  const c2g = {};
  for (const item of data) {
    if (item.carrierName && item.carrierCode) {
      const display = `${item.carrierName} (${item.carrierCode})`;
      map[display] = item.carrierCode;
      names.push(display);
    }
    if (item.carrierName && Array.isArray(item.rxGroups)) {
      c2g[`${item.carrierName} (${item.carrierCode})`] = item.rxGroups
        .filter((g) => g?.groupNumber && g?.groupName)
        .map((g) => ({
          groupNumber: g.groupNumber,
          groupName: g.groupName,
        }));
    }
  }
  return { map, names, c2g };
};

const buildImpliedMaps = (c2g, carrierNameToIdMap) => {
  const impliedGroupCodeMap = {};
  const impliedGroupDisplayMap = {};
  for (const carrierDisplay of Object.keys(c2g)) {
    const carrierCode =
      extractLastParenValue(carrierDisplay) ??
      carrierNameToIdMap?.[carrierDisplay];
    for (const g of c2g[carrierDisplay] || []) {
      if (g?.groupNumber) {
        impliedGroupCodeMap[String(g.groupNumber)] = carrierCode;
        impliedGroupDisplayMap[String(g.groupNumber)] = carrierDisplay;
      }
    }
  }
  return { impliedGroupCodeMap, impliedGroupDisplayMap };
};

const processCarrierOptions = async (
  ot,
  carrierNameToIdMap,
  search,
  claimType,
) => {
  const params = { outreachType: ot };
  if (claimType) params.claim_type = claimType;
  if (typeof search === "string" && search.trim() !== "") {
    params.search = search.trim();
  }
  const data = await fetchCarriers(params);
  const { map, names, c2g } = parseCarrierData(data);
  const { impliedGroupCodeMap, impliedGroupDisplayMap } = buildImpliedMaps(
    c2g,
    carrierNameToIdMap,
  );

  return { map, names, c2g, impliedGroupCodeMap, impliedGroupDisplayMap };
};

const processGroupOptions = async (ot, search, claimType) => {
  const params = { outreachType: ot };
  if (claimType) params.claim_type = claimType;
  if (typeof search === "string" && search.trim() !== "") {
    params.search = search.trim();
  }
  const data = await fetchGroups(params);
  const map = {};
  const names = [];
  const carrierCodeMap = {};
  const carrierDisplayMap = {};
  for (const item of data) {
    if (item.groupName && item.groupNumber) {
      const display = `${item.groupName} (${item.groupNumber})`;
      map[display] = item.groupNumber;
      names.push(display);
      if (item.carrierCode && item.carrierName) {
        carrierCodeMap[item.groupNumber] = item.carrierCode;
        carrierDisplayMap[item.groupNumber] =
          `${item.carrierName} (${item.carrierCode})`;
      }
    }
  }
  return { map, names, carrierCodeMap, carrierDisplayMap };
};

const processAssigneeOptions = async (ot, search, claimType) => {
  const params = { outreachType: ot };
  if (claimType) params.claim_type = claimType;
  if (typeof search === "string" && search.trim() !== "") {
    params.search = search.trim();
  }
  const data = await fetchAssignees(params);
  const map = {};
  const names = [];
  for (const item of data) {
    if (item.assigneeName && item.assigneeId) {
      map[item.assigneeName] = item.assigneeId;
      names.push(item.assigneeName);
    }
  }
  return { map, names };
};

const mergeDropdownList = (currentList, displayList) => {
  const arr = Array.isArray(currentList) ? currentList : [];
  const remaining = arr.filter((item) => !displayList.includes(item));
  const newArr = [...displayList, ...remaining];

  if (arr.length === newArr.length && arr.every((v, i) => v === newArr[i])) {
    return arr;
  }
  return newArr;
};

const fetchAndMergeGroups = async (
  fetchOptionsForType,
  setGroups,
  displaysArr,
) => {
  try {
    await fetchOptionsForType("GROUP", "", "", true);
    setGroups((loaded) => mergeDropdownList(loaded, displaysArr));
  } catch (e) {
    console.error("Groups fetch failed for carrier sync:", e);
  }
};

const fetchAndMergeCarriers = async (
  fetchOptionsForType,
  setCarrier,
  carrierDisplays,
) => {
  try {
    await fetchOptionsForType("CARRIER", "", "", true);
    setCarrier((loaded) => mergeDropdownList(loaded, carrierDisplays));
  } catch (e) {
    console.warn("Carrier fetch failed for group sync:", e);
  }
};

const arraysHaveSameValues = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((v) => setA.has(v));
};

const syncGroupSelectionsFromCarriers = (
  groupFilter,
  selectedGroupIds,
  displaysArr,
  idsArr,
  carrierSelectionTriggerRef,
  dispatch,
) => {
  const sameGroups = arraysHaveSameValues(groupFilter, displaysArr);
  const sameIds = arraysHaveSameValues(selectedGroupIds, idsArr);

  if (carrierSelectionTriggerRef.current) {
    if (!sameGroups || !sameIds) {
      dispatch(
        setMembersFilters({
          groupFilter: displaysArr,
          selectedGroupIds: idsArr,
        }),
      );
    }
    carrierSelectionTriggerRef.current = false;
    return;
  }

  const hasExistingGroupSelection =
    Array.isArray(groupFilter) && groupFilter.length > 0;

  if (hasExistingGroupSelection && !sameGroups) {
    const isSubset = groupFilter.every((g) => displaysArr.includes(g));
    if (isSubset) {
      return;
    }
  }

  if (!sameGroups || !sameIds) {
    dispatch(
      setMembersFilters({
        groupFilter: displaysArr,
        selectedGroupIds: idsArr,
      }),
    );
  }
};

const useMembersUI = (isPriorityOutreach, isMemberMonitoring) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [quickViewMember, setQuickViewMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const resultsContainerRef = useRef(null);
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh =
        globalThis.window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        globalThis.window.innerWidth <= 1440 &&
        globalThis.window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedTableHeight(h);
    };

    computeHeight();
    globalThis.window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      if (document.body) ro.observe(document.body);
    } catch (error) {
      console.debug("ResizeObserver loop limit exceeded:", error);
    }
    return () => {
      globalThis.window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, [filtersVisible]);

  const handleQuickView = useCallback(
    (member) => {
      if (!member) return;

      const memberId = member.member?.memberId || member.memberId || member.id;

      if (memberId) {
        let navigationSourceType = "members";
        if (isMemberMonitoring) {
          navigationSourceType = "monitoring";
        } else if (isPriorityOutreach) {
          navigationSourceType = "member-outreach";
        }

        const cleanMember = {
          memberId: memberId,
          id: memberId,
          name: member.member?.name || member.name || "",
          language: member.member?.language || member.language || "",
          timeZone: member.member?.timeZone || member.timeZone || "",
          gender: member.member?.gender || member.gender || "",
          age: member.member?.age || member.age || "",
          dob: member.member?.dob || member.dob || "",
          dateOfBirth: member.member?.dateOfBirth || member.dateOfBirth || "",
          knownAllergies:
            member.member?.knownAllergies || member.knownAllergies || [],
          allergies: member.member?.allergies || member.allergies || [],
          status: member.member?.status || member.status || "Active",
          contact: member.member?.contact || member.contact || {},

          navigationSource: {
            fromMemberMonitoring: isMemberMonitoring,
            fromPriorityOutreach: isPriorityOutreach,
            source: navigationSourceType,
          },
        };

        setSelectedMember(cleanMember);
        setQuickViewMember(true);
      } else {
        setQuickViewMember(false);
      }
    },
    [isMemberMonitoring, isPriorityOutreach],
  );

  const handleCloseQuickView = useCallback(() => {
    setQuickViewMember(null);
    setSelectedMember(null);
  }, []);

  return {
    isMobile,
    filtersVisible,
    setFiltersVisible,
    quickViewMember,
    selectedMember,
    computedTableHeight,
    resultsContainerRef,
    containerScale,
    handleQuickView,
    handleCloseQuickView,
  };
};

const useMembersFilter = (isPriorityOutreach, isMemberMonitoring) => {
  const dispatch = useDispatch();
  const membersFiltersState = useSelector((state) => state.membersFilters);

  const programFilter = membersFiltersState.programFilter;
  const carrierFilter = membersFiltersState.carrierFilter;
  const groupFilter = membersFiltersState.groupFilter;
  const assigneeFilter = membersFiltersState.assigneeFilter;
  const dispositionFilter = membersFiltersState.dispositionFilter;
  const carrier = membersFiltersState.carrier;
  const groups = membersFiltersState.groups;
  const assignees = membersFiltersState.assignees;
  const programs = membersFiltersState.programs;
  const recentActivityOutcomeFilter = useMemo(
    () => membersFiltersState.recentActivityOutcomeFilter || [],
    [membersFiltersState.recentActivityOutcomeFilter],
  );
  const recentActivityReasonFilter = useMemo(
    () => membersFiltersState.recentActivityReasonFilter || [],
    [membersFiltersState.recentActivityReasonFilter],
  );
  const recentActivityOutcomesList = useMemo(
    () => membersFiltersState.recentActivityOutcomesList || [],
    [membersFiltersState.recentActivityOutcomesList],
  );

  const [dropdownLoading, setDropdownLoading] = useState({
    PROGRAM: false,
    DISPOSITION: false,
    ASSIGNEE: false,
    GROUP: false,
    CARRIER: false,
    RECENT_ACTIVITY: false,
  });
  const [dropdownHasMore, setDropdownHasMore] = useState({
    PROGRAM: true,
    DISPOSITION: false,
    ASSIGNEE: true,
    GROUP: true,
    CARRIER: true,
    RECENT_ACTIVITY: false,
  });

  const searchRef = useRef(null);
  const isSearchTriggered = useRef(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const searchText = membersFiltersState.searchText;

  const selectedProgramIds = membersFiltersState.selectedProgramIds;
  const selectedAssigneeIds = membersFiltersState.selectedAssigneeIds;
  const selectedGroupIds = membersFiltersState.selectedGroupIds;
  const selectedCarrierIds = membersFiltersState.selectedCarrierIds;
  const selectedDispositionIds = membersFiltersState.selectedDispositionIds;

  const programNameToIdMap = membersFiltersState.programNameToIdMap;
  const [carrierNameToIdMap, setCarrierNameToIdMap] = useState({});
  const [groupNameToIdMap, setGroupNameToIdMap] = useState({});
  const assigneeNameToIdMap = membersFiltersState.assigneeNameToIdMap;
  // Carrier/group relationship maps are stored in Redux so they
  // persist across navigation. Local state is only for name->id
  // mappings needed when converting dropdown selections to IDs.
  const carrierToGroupsMap = useMemo(
    () => membersFiltersState.carrierToGroupsMap || {},
    [membersFiltersState.carrierToGroupsMap],
  );
  const groupNumberToCarrierCode = useMemo(
    () => membersFiltersState.groupNumberToCarrierCode || {},
    [membersFiltersState.groupNumberToCarrierCode],
  );
  const groupNumberToCarrierDisplay = useMemo(
    () => membersFiltersState.groupNumberToCarrierDisplay || {},
    [membersFiltersState.groupNumberToCarrierDisplay],
  );
  const programFilterSearch = membersFiltersState.programFilterSearch;
  const assigneeFilterSearch = membersFiltersState.assigneeFilterSearch;
  const groupFilterSearch = membersFiltersState.groupFilterSearch;
  const carrierFilterSearch = membersFiltersState.carrierFilterSearch;
  const claimType = membersFiltersState.claimType || "PAID";

  // Derive display names and nested subreason map from raw outcomes list
  const recentActivityOutcomeNames = useMemo(
    () =>
      recentActivityOutcomesList
        .map((o) => getRecentActivityLabel(o))
        .filter(Boolean),
    [recentActivityOutcomesList],
  );
  const recentActivityNestedOptions = useMemo(() => {
    const map = {};
    recentActivityOutcomesList.forEach((o) => {
      const label = getRecentActivityLabel(o);
      const children = getRecentActivityChildren(o)
        .map((s) => getRecentActivityLabel(s))
        .filter(Boolean);
      if (label && children.length > 0) {
        map[label] = children;
      }
    });
    return map;
  }, [recentActivityOutcomesList]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;
  const isL6L7 = role === "L6" || role === "L7";

  // Handlers extracted from fetchOptionsForType to reduce cognitive complexity
  const handleProgramType = useCallback(
    async (ot, normalizedSearch, replace) => {
      const { map, names } = await processProgramOptions(
        ot,
        normalizedSearch,
        isL6L7 ? claimType : undefined,
      );
      const mergedProgramMap =
        replace || !programNameToIdMap
          ? map
          : { ...programNameToIdMap, ...map };
      const nextPrograms = mergeDropdownOptions(replace, programs, names);
      dispatch(
        setMembersFilters({
          programs: nextPrograms,
          programNameToIdMap: mergedProgramMap,
        }),
      );
    },
    [programNameToIdMap, programs, dispatch, claimType, isL6L7],
  );

  const handleCarrierType = useCallback(
    async (ot, normalizedSearch, replace) => {
      const { map, names, c2g, impliedGroupCodeMap, impliedGroupDisplayMap } =
        await processCarrierOptions(
          ot,
          carrierNameToIdMap,
          normalizedSearch,
          isL6L7 ? claimType : undefined,
        );

      setCarrierNameToIdMap((prev) => (replace ? map : { ...prev, ...map }));
      const nextCarrier = mergeDropdownOptions(replace, carrier, names);
      const mergedCarrierToGroups = { ...carrierToGroupsMap, ...c2g };
      const mergedGroupNumberToCarrierCode = {
        ...impliedGroupCodeMap,
        ...groupNumberToCarrierCode,
      };
      const mergedGroupNumberToCarrierDisplay = {
        ...impliedGroupDisplayMap,
        ...groupNumberToCarrierDisplay,
      };
      dispatch(
        setMembersFilters({
          carrier: nextCarrier,
          carrierToGroupsMap: mergedCarrierToGroups,
          groupNumberToCarrierCode: mergedGroupNumberToCarrierCode,
          groupNumberToCarrierDisplay: mergedGroupNumberToCarrierDisplay,
        }),
      );
    },
    [
      carrierNameToIdMap,
      carrier,
      carrierToGroupsMap,
      groupNumberToCarrierCode,
      groupNumberToCarrierDisplay,
      dispatch,
      setCarrierNameToIdMap,
      claimType,
      isL6L7,
    ],
  );

  const handleGroupType = useCallback(
    async (ot, normalizedSearch, replace) => {
      const { map, names, carrierCodeMap, carrierDisplayMap } =
        await processGroupOptions(
          ot,
          normalizedSearch,
          isL6L7 ? claimType : undefined,
        );
      setGroupNameToIdMap((prev) => (replace ? map : { ...prev, ...map }));
      const mergedGroupNumberToCarrierCode = {
        ...groupNumberToCarrierCode,
        ...carrierCodeMap,
      };
      const mergedGroupNumberToCarrierDisplay = {
        ...groupNumberToCarrierDisplay,
        ...carrierDisplayMap,
      };
      const nextGroups = mergeDropdownOptions(replace, groups, names);
      dispatch(
        setMembersFilters({
          groups: nextGroups,
          groupNumberToCarrierCode: mergedGroupNumberToCarrierCode,
          groupNumberToCarrierDisplay: mergedGroupNumberToCarrierDisplay,
        }),
      );
    },
    [
      groupNumberToCarrierCode,
      groupNumberToCarrierDisplay,
      groups,
      dispatch,
      setGroupNameToIdMap,
      claimType,
      isL6L7,
    ],
  );

  const handleAssigneeType = useCallback(
    async (ot, normalizedSearch, replace) => {
      const { map, names } = await processAssigneeOptions(
        ot,
        normalizedSearch,
        isL6L7 ? claimType : undefined,
      );
      const mergedAssigneeMap =
        replace || !assigneeNameToIdMap
          ? map
          : { ...assigneeNameToIdMap, ...map };
      const nextAssignees = mergeDropdownOptions(replace, assignees, names);
      dispatch(
        setMembersFilters({
          assignees: nextAssignees,
          assigneeNameToIdMap: mergedAssigneeMap,
        }),
      );
    },
    [assigneeNameToIdMap, assignees, dispatch, claimType, isL6L7],
  );

  const dropdownInFlightRef = useRef({});
  const isGroupDrivenChangeRef = useRef(false);
  const skipCarrierToGroupSyncRef = useRef(false);
  const isCarrierDropdownSearchRef = useRef(false);
  const didInitCarrierGroupSyncRef = useRef(false);
  const carrierSelectionTriggerRef = useRef(false);

  // Refs shared with data fetching
  const suppressNextTableFetchRef = useRef(false);
  const carrierSelectionIsManualRef = useRef(false);
  const programSelectionTriggerRef = useRef(false);
  const assigneeSelectionTriggerRef = useRef(false);
  const dispositionSelectionTriggerRef = useRef(false);

  const countActiveFilters = useCallback(() => {
    // Only count a parent outcome if none of its nested subreasons are selected
    const outcomesToCount = (recentActivityOutcomeFilter || []).filter(
      (outcome) => {
        const children = recentActivityNestedOptions?.[outcome] || [];
        const hasAnyChildSelected = children.some((child) =>
          (recentActivityReasonFilter || []).includes(child),
        );
        return !hasAnyChildSelected;
      },
    );

    return [
      ...(programFilter || []),
      ...(assigneeFilter || []),
      ...(dispositionFilter || []),
      ...(groupFilter || []),
      ...(carrierFilter || []),
      ...outcomesToCount,
      ...(recentActivityReasonFilter || []),
    ].filter(Boolean).length;
  }, [
    programFilter,
    dispositionFilter,
    assigneeFilter,
    groupFilter,
    carrierFilter,
    recentActivityOutcomeFilter,
    recentActivityReasonFilter,
    recentActivityNestedOptions,
  ]);

  const activeFilterCount = countActiveFilters();
  const activeFilterDigits = String(activeFilterCount).length;

  // Memoized so the same debounced function persists across renders - a
  // fresh _.debounce(...) created on every render doesn't cancel the
  // previous instance's pending timer (it keeps running detached), so
  // typing while the page re-renders could fire several independent
  // dispatches for different partial search strings instead of one.
  const handleSearch = useMemo(
    () =>
      _.debounce((text) => {
        dispatch(
          setMembersFilters({
            searchText: text,
          }),
        );
        isSearchTriggered.current = true;
        if (text === "") {
          setIsResetSearch(true);
          setTimeout(() => setIsResetSearch(false), 100);
        }
      }, 500),
    [dispatch],
  );

  useEffect(() => () => handleSearch.cancel(), [handleSearch]);

  const onDropdownSearch = (term, type) => {
    if (!type) return;
    const searchVal = (term || "").trim();

    if (type === "PROGRAM")
      dispatch(setMembersFilters({ programFilterSearch: searchVal }));
    if (type === "ASSIGNEE")
      dispatch(setMembersFilters({ assigneeFilterSearch: searchVal }));
    if (type === "GROUP")
      dispatch(setMembersFilters({ groupFilterSearch: searchVal }));
    if (type === "CARRIER")
      dispatch(setMembersFilters({ carrierFilterSearch: searchVal }));
    return undefined;
  };

  const fetchOptionsForType = useCallback(
    async (type, outreachTypeOverride, search = "", replace = false) => {
      const ot =
        outreachTypeOverride ||
        determineOutreachType(isPriorityOutreach, isMemberMonitoring);
      const key = `${type}:${ot}`;
      if (dropdownInFlightRef.current[key]) return;
      dropdownInFlightRef.current[key] = true;

      const normalizedSearch = typeof search === "string" ? search.trim() : "";
      const fetchers = {
        PROGRAM: () => handleProgramType(ot, normalizedSearch, replace),
        CARRIER: () => handleCarrierType(ot, normalizedSearch, replace),
        GROUP: () => handleGroupType(ot, normalizedSearch, replace),
        ASSIGNEE: () => handleAssigneeType(ot, normalizedSearch, replace),
        RECENT_ACTIVITY: async () => {
          const isL9 = isUserL9Role();
          const response = isL9
            ? await fetchAssessmentReferenceData({ source: "outreach" })
            : await getOutcomesOutreach({ source: "outreach" });
          const outcomes = normalizeRecentActivityOutcomes(response);
          dispatch(
            setMembersFilters({
              recentActivityOutcomesList: outcomes,
            }),
          );
        },
      };

      setDropdownLoading((m) => ({ ...m, [type]: true }));
      try {
        await fetchers[type]?.();
        setDropdownHasMore((m) => ({ ...m, [type]: false }));
      } catch (e) {
        console.error("Dropdown options fetch failed:", e);
        setDropdownHasMore((m) => ({ ...m, [type]: false }));
      } finally {
        setDropdownLoading((m) => ({ ...m, [type]: false }));
        delete dropdownInFlightRef.current[key];
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isPriorityOutreach,
      isMemberMonitoring,
      carrierNameToIdMap,
      programNameToIdMap,
      programs,
      carrier,
      groups,
      assignees,
      assigneeNameToIdMap,
      carrierToGroupsMap,
      groupNumberToCarrierCode,
      groupNumberToCarrierDisplay,
      dispatch,
      handleProgramType,
      handleCarrierType,
      handleGroupType,
      handleAssigneeType,
    ],
  );

  const handleDropdownOpen = useCallback(
    async (type) => {
      if (!type) return;

      const haveOptions =
        (type === "PROGRAM" && programs.length > 0) ||
        (type === "CARRIER" && carrier.length > 0) ||
        (type === "GROUP" && groups.length > 0) ||
        (type === "ASSIGNEE" && assignees.length > 0) ||
        (type === "RECENT_ACTIVITY" && recentActivityOutcomesList.length > 0);
      if (!haveOptions) {
        if (type === "GROUP") {
          await Promise.all([
            fetchOptionsForType("GROUP", "", "", true),
            fetchOptionsForType("CARRIER", "", "", true),
          ]);
        } else if (type === "CARRIER") {
          await Promise.all([
            fetchOptionsForType("CARRIER", "", "", true),
            fetchOptionsForType("GROUP", "", "", true),
          ]);
        } else {
          await fetchOptionsForType(type, "", "", true);
        }
      }
    },
    [
      programs.length,
      assignees.length,
      groups.length,
      carrier.length,
      recentActivityOutcomesList.length,
      fetchOptionsForType,
    ],
  );

  const handleDropdownLoadMore = useCallback(
    async (type) => {
      if (!type) return;
      if (dropdownLoading[type]) return;
      if (!dropdownHasMore[type]) return;
      await fetchOptionsForType(type, "", "", false);
    },
    [dropdownLoading, dropdownHasMore, fetchOptionsForType],
  );

  useEffect(() => {
    const onAssigneeUpdated = () => {
      setDropdownHasMore((m) => ({ ...m, ASSIGNEE: true }));
      fetchOptionsForType("ASSIGNEE", "", "", true);
    };
    if (globalThis.window?.addEventListener) {
      globalThis.window.addEventListener("assignee-updated", onAssigneeUpdated);
    }
    return () => {
      globalThis.window?.removeEventListener?.(
        "assignee-updated",
        onAssigneeUpdated,
      );
    };
  }, [fetchOptionsForType]);

  // No extra refetch needed on remount: carrier/group relationship
  // maps now live in Redux and are restored with the rest of the
  // filter state by redux-persist.

  const handleProgramChange = (selectedProgramNames) => {
    let selected = [];
    if (Array.isArray(selectedProgramNames)) {
      selected = selectedProgramNames;
    } else if (selectedProgramNames) {
      selected = [selectedProgramNames];
    }

    const selectedIds = selected
      .map((name) => programNameToIdMap[name])
      .filter(Boolean);
    dispatch(
      setMembersFilters({
        programFilter: selected,
        selectedProgramIds: selectedIds,
      }),
    );
    programSelectionTriggerRef.current = true;
    suppressNextTableFetchRef.current = false;
  };

  const statusMap = {
    Complete: "COMPLETE",
    Incomplete: "INCOMPLETE",
  };

  const handleDispositionChange = (selected) => {
    const mapped = selected.map((s) => statusMap[s] || s);
    dispatch(
      setMembersFilters({
        dispositionFilter: selected,
        selectedDispositionIds: mapped,
      }),
    );
    // mark that this change should trigger a table refresh
    dispositionSelectionTriggerRef.current = true;
    suppressNextTableFetchRef.current = false;
  };

  const handleRecentActivityChange = (selectedOutcomes, selectedReasons) => {
    dispatch(
      setMembersFilters({
        recentActivityOutcomeFilter: selectedOutcomes || [],
        recentActivityReasonFilter: selectedReasons || [],
      }),
    );
    dispositionSelectionTriggerRef.current = true;
    suppressNextTableFetchRef.current = false;
  };

  const handleCarrierChange = (selectedCarrierNames) => {
    let selected = [];
    if (Array.isArray(selectedCarrierNames)) {
      selected = selectedCarrierNames;
    } else if (selectedCarrierNames) {
      selected = [selectedCarrierNames];
    }

    // Extract value inside last parentheses without regex (safe)
    const selectedCodes = selected
      .map((display) => {
        const code =
          extractLastParenValue(display) ?? carrierNameToIdMap[display];
        if (code && /^\d+$/.test(code)) {
          return Number(code);
        }
        return code;
      })
      .filter(Boolean);

    const nextFilters = {
      carrierFilter: selected,
      selectedCarrierIds: selectedCodes,
    };

    // When all carriers are cleared, also clear any group filters/ids
    // so the request body does not keep stale group selections.
    if (selected.length === 0) {
      nextFilters.groupFilter = [];
      nextFilters.selectedGroupIds = [];
    }

    dispatch(setMembersFilters(nextFilters));
    carrierSelectionTriggerRef.current = true;

    try {
      const hasGroups = selected.some((display) => {
        const groupsForCarrier = carrierToGroupsMap[display] || [];
        return groupsForCarrier.length > 0;
      });
      suppressNextTableFetchRef.current = !!hasGroups;
      carrierSelectionIsManualRef.current = !hasGroups;
    } catch (e) {
      console.error("Carrier change sync failed:", e);
      suppressNextTableFetchRef.current = false;
      carrierSelectionIsManualRef.current = true;
    }
  };
  const shouldResetGroups = (
    selectedCarrierIds,
    groupFilter,
    selectedGroupIds,
  ) =>
    (!Array.isArray(selectedCarrierIds) || selectedCarrierIds.length === 0) &&
    (groupFilter?.length || selectedGroupIds?.length);

  const isMappingNotReady = (carrierToGroupsMap) =>
    !carrierToGroupsMap || Object.keys(carrierToGroupsMap).length === 0;

  const deriveGroupsFromCarriers = (carrierFilter, carrierToGroupsMap) => {
    const groupDisplays = new Set();
    const groupIds = new Set();

    for (const carrierDisplay of carrierFilter) {
      const groupsForCarrier = carrierToGroupsMap[carrierDisplay] || [];

      for (const { groupNumber, groupName } of groupsForCarrier) {
        const display = `${groupName} (${groupNumber})`;
        groupDisplays.add(display);

        if (groupNumber != null) {
          groupIds.add(String(groupNumber));
        }
      }
    }

    return {
      displaysArr: Array.from(groupDisplays),
      idsArr: Array.from(groupIds),
    };
  };

  const handleCarrierGroupEarlyExits = ({
    didInitCarrierGroupSyncRef,
    isCarrierDropdownSearchRef,
    skipCarrierToGroupSyncRef,
    isGroupDrivenChangeRef,
    selectedCarrierIds,
    groupFilter,
    selectedGroupIds,
    dispatch,
    shouldResetGroupsFn,
  }) => {
    if (!didInitCarrierGroupSyncRef.current) {
      didInitCarrierGroupSyncRef.current = true;
      return true;
    }

    if (isCarrierDropdownSearchRef.current) {
      return true;
    }

    if (skipCarrierToGroupSyncRef.current) {
      skipCarrierToGroupSyncRef.current = false;
      isGroupDrivenChangeRef.current = false;
      return true;
    }

    if (isGroupDrivenChangeRef.current) {
      isGroupDrivenChangeRef.current = false;
      return true;
    }

    if (
      shouldResetGroupsFn(selectedCarrierIds, groupFilter, selectedGroupIds)
    ) {
      dispatch(
        setMembersFilters({
          groupFilter: [],
          selectedGroupIds: [],
        }),
      );
      return true;
    }

    return false;
  };

  const updateGroupsDropdownFromCarriers = (
    currentGroups,
    displaysArr,
    fetchOptionsForTypeFn,
    dispatchFn,
  ) => {
    const prevArr = Array.isArray(currentGroups) ? currentGroups : [];

    if (prevArr.length === 0) {
      void fetchAndMergeGroups(
        fetchOptionsForTypeFn,
        (loaded) => {
          const merged = mergeDropdownList(loaded, displaysArr);
          dispatchFn(setMembersFilters({ groups: merged }));
        },
        displaysArr,
      );
    } else {
      const merged = mergeDropdownList(prevArr, displaysArr);
      dispatchFn(setMembersFilters({ groups: merged }));
    }
  };

  useEffect(() => {
    const shouldExit = handleCarrierGroupEarlyExits({
      didInitCarrierGroupSyncRef,
      isCarrierDropdownSearchRef,
      skipCarrierToGroupSyncRef,
      isGroupDrivenChangeRef,
      selectedCarrierIds,
      groupFilter,
      selectedGroupIds,
      dispatch,
      shouldResetGroupsFn: shouldResetGroups,
    });

    if (shouldExit) return;

    if (!Array.isArray(carrierFilter) || carrierFilter.length === 0) return;
    if (isMappingNotReady(carrierToGroupsMap)) return;

    const { displaysArr, idsArr } = deriveGroupsFromCarriers(
      carrierFilter,
      carrierToGroupsMap,
    );
    updateGroupsDropdownFromCarriers(
      groups,
      displaysArr,
      fetchOptionsForType,
      dispatch,
    );

    syncGroupSelectionsFromCarriers(
      groupFilter,
      selectedGroupIds,
      displaysArr,
      idsArr,
      carrierSelectionTriggerRef,
      dispatch,
    );
  }, [
    carrierFilter,
    selectedCarrierIds, // ✅ add this
    carrierToGroupsMap,
    fetchOptionsForType,
    dispatch,
    groupFilter,
    selectedGroupIds,
    groups,
  ]);

  const normalizeSelectedGroups = (selectedGroupNames) => {
    if (Array.isArray(selectedGroupNames)) return selectedGroupNames;
    if (selectedGroupNames) return [selectedGroupNames];
    return [];
  };

  const getSelectedGroupNumbers = (selected) =>
    selected
      .map((display) => {
        const num = extractLastParenValue(display) ?? groupNameToIdMap[display];
        return num == null ? num : String(num);
      })
      .filter(Boolean);

  const resolveCarriersFromDirectMap = (selectedNumbers) => {
    const displaySet = new Set();
    const codeSet = new Set();

    for (const num of selectedNumbers) {
      const disp = groupNumberToCarrierDisplay?.[num];
      const code = groupNumberToCarrierCode?.[num];

      if (disp && code) {
        displaySet.add(disp);
        codeSet.add(String(code));
      }
    }

    return { displaySet, codeSet };
  };

  const resolveCarriersFromFallback = (
    selectedNumbers,
    carrierDisplaySet,
    carrierCodeSet,
  ) => {
    if (carrierDisplaySet.size > 0 || selectedNumbers.length === 0) return;

    const selectedSet = new Set(selectedNumbers.map(String));

    for (const [carrierDisplay, groupsForCarrier] of Object.entries(
      carrierToGroupsMap || {},
    )) {
      const groupsArr = Array.isArray(groupsForCarrier) ? groupsForCarrier : [];

      const match = groupsArr.some((g) =>
        selectedSet.has(String(g?.groupNumber)),
      );

      if (match) {
        carrierDisplaySet.add(carrierDisplay);

        const inferredCode =
          extractLastParenValue(carrierDisplay) ||
          carrierNameToIdMap[carrierDisplay];

        if (inferredCode) {
          carrierCodeSet.add(String(inferredCode));
        }
      }
    }
  };

  const handleGroupChange = (selectedGroupNames) => {
    const selected = normalizeSelectedGroups(selectedGroupNames);
    const selectedNumbers = getSelectedGroupNumbers(selected);

    dispatch(
      setMembersFilters({
        groupFilter: selected,
        selectedGroupIds: selectedNumbers,
      }),
    );

    const { displaySet: carrierDisplaySet, codeSet: carrierCodeSet } =
      resolveCarriersFromDirectMap(selectedNumbers);

    resolveCarriersFromFallback(
      selectedNumbers,
      carrierDisplaySet,
      carrierCodeSet,
    );

    const carrierDisplays = Array.from(carrierDisplaySet);
    const carrierCodes = Array.from(carrierCodeSet);

    const addMap = {};
    for (const [idx, disp] of carrierDisplays.entries()) {
      addMap[disp] = carrierCodes[idx];
    }

    setCarrierNameToIdMap((prev) => ({ ...prev, ...addMap }));

    if (selectedNumbers.length > 0 && carrierDisplays.length === 0) {
      suppressNextTableFetchRef.current = false;
      isGroupDrivenChangeRef.current = false;
      skipCarrierToGroupSyncRef.current = false;
      return;
    }

    isGroupDrivenChangeRef.current = selectedNumbers.length > 0;

    const prevCarrier = Array.isArray(carrier) ? carrier : [];

    if (prevCarrier.length === 0) {
      void fetchAndMergeCarriers(
        fetchOptionsForType,
        (loaded) => {
          const merged = mergeDropdownList(loaded, carrierDisplays);
          dispatch(setMembersFilters({ carrier: merged }));
        },
        carrierDisplays,
      );
    } else {
      const merged = mergeDropdownList(prevCarrier, carrierDisplays);
      dispatch(setMembersFilters({ carrier: merged }));
    }

    skipCarrierToGroupSyncRef.current = true;
    suppressNextTableFetchRef.current = false;

    dispatch(
      setMembersFilters({
        carrierFilter: carrierDisplays,
        selectedCarrierIds: carrierCodes,
      }),
    );
  };

  const handleAssigneeChange = (selectedAssigneeNames) => {
    let selected = [];
    if (Array.isArray(selectedAssigneeNames)) {
      selected = selectedAssigneeNames;
    } else if (selectedAssigneeNames) {
      selected = [selectedAssigneeNames];
    }

    let selectedIds = [];
    if (selected.includes("Unassigned")) {
      selectedIds.push(0);
    }
    selectedIds.push(
      ...selected
        .filter((name) => name !== "Unassigned")
        .map((name) => assigneeNameToIdMap[name])
        .filter(Boolean),
    );
    dispatch(
      setMembersFilters({
        assigneeFilter: selected,
        selectedAssigneeIds: selectedIds,
      }),
    );
    assigneeSelectionTriggerRef.current = true;
    suppressNextTableFetchRef.current = false;
  };

  const handleClearFilters = useCallback(
    (newClaimType) => {
      dispatch(resetMembersFilters());
      setIsResetSearch(true);
      setFilterResetKey((prev) => prev + 1);
      setTimeout(() => setIsResetSearch(false), 100);
    },
    [dispatch],
  );

  return {
    programFilter,
    carrierFilter,
    groupFilter,
    assigneeFilter,
    dispositionFilter,
    carrier,
    groups,
    assignees,
    programs,
    programFilterSearch,
    carrierFilterSearch,
    groupFilterSearch,
    assigneeFilterSearch,
    dropdownLoading,
    dropdownHasMore,
    activeFilterCount,
    activeFilterDigits,

    selectedProgramIds,
    selectedAssigneeIds,
    selectedGroupIds,
    selectedCarrierIds,
    selectedDispositionIds,

    searchText,
    isResetSearch,
    filterResetKey,
    searchRef,
    isSearchTriggered,

    handleSearch,
    onDropdownSearch,
    handleDropdownOpen,
    handleDropdownLoadMore,
    fetchOptionsForType,

    handleProgramChange,
    handleCarrierChange,
    handleGroupChange,
    handleAssigneeChange,
    handleDispositionChange,
    handleClearFilters,
    claimType,
    assigneeNameToIdMap,
    sorting: membersFiltersState.sorting,

    // Recent activity
    recentActivityOutcomeFilter,
    recentActivityReasonFilter,
    recentActivityOutcomeNames,
    recentActivityNestedOptions,
    handleRecentActivityChange,
    recentActivityOutcomesList,

    // Refs
    suppressNextTableFetchRef,
    carrierSelectionIsManualRef,
    programSelectionTriggerRef,
    assigneeSelectionTriggerRef,
    dispositionSelectionTriggerRef,
    dropdownInFlightRef,
  };
};

const useMembersData = (
  {
    isPriorityOutreach,
    isMemberMonitoring,
    selectedProgramIds,
    selectedAssigneeIds,
    selectedGroupIds,
    selectedCarrierIds,
    selectedDispositionIds,
    searchText,
    claimType,
    pageSize,
  },
  {
    suppressNextTableFetchRef,
    carrierSelectionIsManualRef,
    programSelectionTriggerRef,
    assigneeSelectionTriggerRef,
    dispositionSelectionTriggerRef,
    showLoading,
    hideLoading,
  },
) => {
  const dispatch = useDispatch();
  const membersFiltersState = useSelector((state) => state.membersFilters);
  const sorting = membersFiltersState.sorting;
  const recentActivityOutcomeFilter = useMemo(
    () => membersFiltersState.recentActivityOutcomeFilter || [],
    [membersFiltersState.recentActivityOutcomeFilter],
  );
  const recentActivityReasonFilter = useMemo(
    () => membersFiltersState.recentActivityReasonFilter || [],
    [membersFiltersState.recentActivityReasonFilter],
  );
  const recentActivityOutcomesList = useMemo(
    () => membersFiltersState.recentActivityOutcomesList || [],
    [membersFiltersState.recentActivityOutcomesList],
  );
  const recentActivityOutcomesListRef = useRef(recentActivityOutcomesList);
  const [results, setResults] = React.useState({
    content: [],
    totalElements: 0,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLastPage, setIsLastPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const isFetchingMoreRef = useRef(false);
  const filterChangeRef = useRef(false);
  const lastFetchSignatureRef = useRef(null);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    recentActivityOutcomesListRef.current = recentActivityOutcomesList;
  }, [recentActivityOutcomesList]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;
  const isL6L7 = role === "L6" || role === "L7";

  const loadInitialData = useCallback(
    async (page = 0, size = 200, search = "", _filters = {}) => {
      try {
        const { signature, queryParams } = buildFetchParams({
          page,
          size,
          search,
          sorting,
          selectedAssigneeIds,
          selectedCarrierIds,
          selectedGroupIds,
          selectedDispositionIds,
          selectedProgramIds,
          recentActivityOutcomeFilter,
          recentActivityReasonFilter,
          recentActivityOutcomesList: recentActivityOutcomesListRef.current,
          isPriorityOutreach,
          isMemberMonitoring,
          isL6L7,
          claimType,
        });

        if (lastFetchSignatureRef.current === signature) {
          setLoading(false);
          hideLoading();
          isFetchingMoreRef.current = false;
          return;
        }

        const mySeq = ++fetchSeqRef.current;
        setLoading(true);
        if (page > 0) isFetchingMoreRef.current = true;
        if (page === 0) showLoading();

        lastFetchSignatureRef.current = signature;

        const response = await fetchPriorityOutreach(queryParams);

        if (mySeq !== fetchSeqRef.current) return;

        const normalized = normalizeResponse(response);
        let mergedLength = normalized.content?.length || 0;

        if (page === 0) {
          setResults(normalized);
        } else {
          setResults((prev) => {
            const mergedContent = [
              ...(prev?.content || []),
              ...(normalized.content || []),
            ];
            mergedLength = mergedContent.length;
            return {
              content: mergedContent,
              totalElements:
                typeof normalized.totalElements === "number"
                  ? normalized.totalElements
                  : prev?.totalElements || 0,
            };
          });
        }
        // Once the backend has returned a short/last page, cap the reported
        // total at exactly what's been accumulated so far - trusting the
        // backend's totalElements here (which counts filtered-out
        // dependents) would leave "loaded so far < total" permanently true
        // and CommonTable's infinite-scroll would keep requesting pages
        // forever past the actual end of the data.
        let nextTotalRows = mergedLength;
        if (
          !normalized.isLastPage &&
          typeof normalized.totalElements === "number"
        ) {
          nextTotalRows = normalized.totalElements;
        }
        setTotalRows(nextTotalRows);
        setIsLastPage(Boolean(normalized.isLastPage));
        setLoading(false);
      } catch (err) {
        console.error("Data fetch failed:", err);
        setLoading(false);
        setError("Failed to load data. Please try again.");
        hideLoading();
      } finally {
        isFetchingMoreRef.current = false;
        filterChangeRef.current = false;
        hideLoading();
      }
    },
    [
      selectedProgramIds,
      selectedAssigneeIds,
      selectedGroupIds,
      selectedCarrierIds,
      selectedDispositionIds,
      recentActivityOutcomeFilter,
      recentActivityReasonFilter,
      sorting,
      isPriorityOutreach,
      isMemberMonitoring,
      showLoading,
      hideLoading,
      claimType,
      isL6L7,
    ],
  );

  useEffect(() => {
    filterChangeRef.current = true;
    setResults({ content: [], totalElements: 0 });
    setTotalRows(0);
    setIsLastPage(false);
    isFetchingMoreRef.current = false;
    setCurrentPage(0);
    lastFetchSignatureRef.current = null;
  }, [
    selectedProgramIds,
    selectedDispositionIds,
    selectedAssigneeIds,
    selectedGroupIds,
    selectedCarrierIds,
    recentActivityOutcomeFilter,
    recentActivityReasonFilter,
    searchText,
    dispatch,
  ]);

  useEffect(
    () => {
      void (async () => {
        if (
          suppressNextTableFetchRef.current &&
          !programSelectionTriggerRef.current &&
          !assigneeSelectionTriggerRef.current &&
          !dispositionSelectionTriggerRef?.current
        ) {
          suppressNextTableFetchRef.current = false;
          carrierSelectionIsManualRef.current = false;
          programSelectionTriggerRef.current = false;
          assigneeSelectionTriggerRef.current = false;
          return;
        }

        if (programSelectionTriggerRef.current)
          programSelectionTriggerRef.current = false;
        if (assigneeSelectionTriggerRef.current)
          assigneeSelectionTriggerRef.current = false;
        if (dispositionSelectionTriggerRef?.current)
          dispositionSelectionTriggerRef.current = false;
        if (filterChangeRef.current && currentPage !== 0) {
          return;
        }
        await loadInitialData(currentPage, pageSize, searchText, {});
      })();
      return () => {};
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentPage,
      pageSize,
      searchText,
      sorting,
      selectedProgramIds,
      selectedAssigneeIds,
      selectedGroupIds,
      selectedDispositionIds,
      selectedCarrierIds,
      recentActivityOutcomeFilter,
      recentActivityReasonFilter,
      loadInitialData,
      claimType,
      isL6L7,
    ],
  );

  useEffect(() => {
    setResults({ content: [], totalElements: 0 });
    setTotalRows(0);
    setIsLastPage(false);
    isFetchingMoreRef.current = false;
    setCurrentPage(0);
    lastFetchSignatureRef.current = null;
  }, [sorting, dispatch]);

  const isItemLoaded = (index) => {
    return !!results.content[index];
  };

  const loadMoreItems = useCallback(
    (startIndex, stopIndex) => {
      if (loading || isFetchingMoreRef.current) return Promise.resolve();
      isFetchingMoreRef.current = true;
      setCurrentPage((prev) => prev + 1);
      return Promise.resolve();
    },
    [loading],
  );

  // Fetches every page after the one currently loaded in one go (no
  // scrolling) and appends them, returning the complete list - used by "Select
  // all" so the selection covers the whole result set. Goes through the same
  // fetch/normalize path as normal paging, then marks the final page as the
  // last-fetched signature so the paging effect doesn't re-fetch it.
  const resultsRef = useRef(results);
  const currentPageRef = useRef(currentPage);
  const isLastPageRef = useRef(isLastPage);
  useEffect(() => {
    resultsRef.current = results;
    currentPageRef.current = currentPage;
    isLastPageRef.current = isLastPage;
  });

  const loadAllRemainingPages = useCallback(async () => {
    const MAX_PAGES = 500;
    // A scroll-triggered page may still be in flight (currentPage already
    // advanced, its rows not yet in results) - let it land first, or its page
    // would be skipped here and the selection would miss those rows.
    const waitForPendingFetch = async (waited = 0) => {
      if (!isFetchingMoreRef.current || waited >= 200) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
      return waitForPendingFetch(waited + 1);
    };
    await waitForPendingFetch();
    await new Promise((resolve) => setTimeout(resolve, 0));

    if (isLastPageRef.current) return resultsRef.current.content || [];

    // Block normal scroll-paging and cancel anything already in flight.
    isFetchingMoreRef.current = true;
    const mySeq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      let page = currentPageRef.current;
      let lastSignature = lastFetchSignatureRef.current;
      const collected = [];
      let reachedEnd = false;

      let lastNormalized = null;

      for (let i = 0; i < MAX_PAGES && !reachedEnd; i += 1) {
        page += 1;
        const { signature, queryParams } = buildFetchParams({
          page,
          size: pageSize,
          search: searchText,
          sorting,
          selectedAssigneeIds,
          selectedCarrierIds,
          selectedGroupIds,
          selectedDispositionIds,
          selectedProgramIds,
          recentActivityOutcomeFilter,
          recentActivityReasonFilter,
          recentActivityOutcomesList: recentActivityOutcomesListRef.current,
          isPriorityOutreach,
          isMemberMonitoring,
          isL6L7,
          claimType,
        });
        const response = await fetchPriorityOutreach(queryParams);
        if (mySeq !== fetchSeqRef.current) return resultsRef.current.content;
        lastNormalized = normalizeResponse(response);
        collected.push(...(lastNormalized.content || []));
        lastSignature = signature;
        reachedEnd =
          Boolean(lastNormalized.isLastPage) || lastNormalized.rawCount === 0;
      }

      const mergedContent = [
        ...(resultsRef.current.content || []),
        ...collected,
      ];
      lastFetchSignatureRef.current = lastSignature;
      let mergedTotalElements = mergedContent.length;
      if (
        typeof resultsRef.current?.totalElements === "number" &&
        resultsRef.current.totalElements > 0
      ) {
        mergedTotalElements = resultsRef.current.totalElements;
      } else if (typeof lastNormalized?.totalElements === "number") {
        mergedTotalElements = lastNormalized.totalElements;
      }
      setResults({
        content: mergedContent,
        totalElements: mergedTotalElements,
      });
      setTotalRows(mergedContent.length);
      setIsLastPage(true);
      setCurrentPage(page);
      return mergedContent;
    } finally {
      isFetchingMoreRef.current = false;
      if (mySeq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    pageSize,
    searchText,
    sorting,
    selectedAssigneeIds,
    selectedCarrierIds,
    selectedGroupIds,
    selectedDispositionIds,
    selectedProgramIds,
    recentActivityOutcomeFilter,
    recentActivityReasonFilter,
    isPriorityOutreach,
    isMemberMonitoring,
    isL6L7,
    claimType,
  ]);

  const handleSetSorting = useCallback(
    (newSorting) => {
      dispatch(setMembersFilters({ sorting: newSorting || [] }));
    },
    [dispatch],
  );

  const refreshData = useCallback(async () => {
    lastFetchSignatureRef.current = null;
    filterChangeRef.current = false;
    isFetchingMoreRef.current = false;
    setResults({ content: [], totalElements: 0 });
    setTotalRows(0);
    setIsLastPage(false);
    if (currentPage !== 0) {
      setCurrentPage(0);
    } else {
      await loadInitialData(0, pageSize, searchText, {});
    }
  }, [currentPage, loadInitialData, pageSize, searchText]);

  return {
    results,
    totalRows,
    isLastPage,
    loading,
    error,
    currentPage,
    setCurrentPage,
    isItemLoaded,
    loadMoreItems,
    loadAllRemainingPages,
    sorting,
    setSorting: handleSetSorting,
    refreshData,
  };
};

const MembersPageHiddenTitle = ({ isPriorityOutreach, isMemberMonitoring }) => {
  const getPageTitle = useCallback(() => {
    if (isPriorityOutreach) return "Member Outreach Members";
    if (isMemberMonitoring) return "Member Monitoring";
    return "Members";
  }, [isPriorityOutreach, isMemberMonitoring]);

  return (
    <h1
      style={{
        position: "absolute",
        left: "-10000px",
        top: "auto",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      {getPageTitle()}
    </h1>
  );
};

MembersPageHiddenTitle.propTypes = {
  isPriorityOutreach: PropTypes.bool,
  isMemberMonitoring: PropTypes.bool,
};

const MembersPageVisibleHeader = ({
  isPriorityOutreach,
  isMemberMonitoring,
  isPhiToggleOn,
  onPhiToggle,
  sectionTitle,
  sectionDescription,
  claimType,
  onClaimTypeChange,
}) => {
  const getBreadcrumbs = useCallback(() => {
    if (isPriorityOutreach) {
      return [{ label: "Member Outreach", path: "" }];
    }
    if (isMemberMonitoring) {
      return [
        { label: "Members", path: "/members" },
        { label: "Member Monitoring", path: "" },
      ];
    }
    return [{ label: "Members", path: "" }];
  }, [isPriorityOutreach, isMemberMonitoring]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          marginBottom: "-10px",
          minHeight: "55px",
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "30px",
          letterSpacing: "0%",
          color: "#080A0E",
          "& a, & span:last-child": {
            color: "#080A0E",
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: "30px",
            letterSpacing: "0%",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
          },
          "& .breadcrumb-separator": {
            margin: "0 4px",
            color: "#080A0E",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {getBreadcrumbs().map((breadcrumb, index) => (
            <React.Fragment key={breadcrumb.label}>
              {index > 0 && <span className="breadcrumb-separator">&gt;</span>}
              {breadcrumb.path ? (
                <Link to={breadcrumb.path}>{breadcrumb.label}</Link>
              ) : (
                <span style={{ cursor: "default" }}>{breadcrumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <div className="search-header" style={{ margin: "0 0 16px 0" }}>
        <PageHeader title={sectionTitle} description={sectionDescription} />
      </div>
    </>
  );
};

MembersPageVisibleHeader.propTypes = {
  isPriorityOutreach: PropTypes.bool,
  isMemberMonitoring: PropTypes.bool,
  isPhiToggleOn: PropTypes.bool,
  onPhiToggle: PropTypes.func,
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
  claimType: PropTypes.string,
  onClaimTypeChange: PropTypes.func,
};

const buildExportQueryParams = ({
  filters,
  isPriorityOutreach,
  isMemberMonitoring,
  isL6L7,
  claimType,
}) => {
  const outreachTypeVal = determineOutreachType(
    isPriorityOutreach,
    isMemberMonitoring,
  );

  const { sortBy: rawSortBy, sortDirection } = getSortParams(
    filters.sorting || [],
  );
  const sortBy =
    outreachTypeVal === "MEMBER_OUTREACH" && rawSortBy === "latestDateAdded"
      ? "dateAdded"
      : rawSortBy;

  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  return {
    outreachType: outreachTypeVal,
    timeZone: userTimeZone,
    ...(filters.searchText?.trim()
      ? { search: filters.searchText.trim() }
      : {}),
    ...(sortBy ? { sortBy } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(isPriorityOutreach && isL6L7 ? { claim_type: claimType } : {}),
  };
};

const buildExportFilename = ({ isPriorityOutreach, isL6L7, claimType }) => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;

  const isRejectToggled =
    isPriorityOutreach && isL6L7 && claimType === "REJECTED";
  return isRejectToggled
    ? `Member_Outreach_CerPassRx_${timestamp}.xlsx`
    : `Member_Outreach_${timestamp}.xlsx`;
};

const pickByViewport = (isMobile, mobileValue, desktopValue) =>
  isMobile ? mobileValue : desktopValue;

const getClaimTypeLabelSx = (isSelected) => ({
  fontSize: "14px",
  fontWeight: isSelected ? 700 : 500,
  color: isSelected ? "#403C6F" : "#7F7C9F",
});

const MembersFilterBar = ({
  ui,
  filters,
  claimType,
  onClaimTypeChange,
  isPriorityOutreach,
  isMemberMonitoring,
  showBulkAssignee,
  bulkAssignee,
  onBulkAssigneeChange,
  bulkAssigneeOptions,
  totalRecords = 0,
  onRefresh,
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;
  const isL6L7 = role === "L6" || role === "L7";

  const [exportLoading, setExportLoading] = useState(false);
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);

  const handleAddPatients = () => {
    // Members are created inside the modal; refresh the table to show them.
    onRefresh?.();
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const queryParams = buildExportQueryParams({
        filters,
        isPriorityOutreach,
        isMemberMonitoring,
        isL6L7,
        claimType,
      });

      const payload = {
        assigneeIds: filters.selectedAssigneeIds || [],
        carrierCodes: filters.selectedCarrierIds || [],
        groupNumbers: filters.selectedGroupIds || [],
        memberStatuses: filters.selectedDispositionIds || [],
        programIds: filters.selectedProgramIds || [],
        ...mapRecentActivitySelections(
          filters.recentActivityOutcomeFilter,
          filters.recentActivityReasonFilter,
          filters.recentActivityOutcomesList,
        ),
      };

      const filename = buildExportFilename({
        isPriorityOutreach,
        isL6L7,
        claimType,
      });

      await downloadExcelFile({
        url: "/outreach/export",
        method: "POST",
        payload,
        queryParams,
        filename,
      });
    } catch (err) {
      console.error("Outreach export failed:", err);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        margin: "0 0 16px 0",
        width: "100%",
        flexWrap: pickByViewport(ui.isMobile, "wrap", "nowrap"),
        gap: pickByViewport(ui.isMobile, "12px", "0px"),
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          width: pickByViewport(ui.isMobile, "100%", "auto"),
          flexWrap: pickByViewport(ui.isMobile, "wrap", "nowrap"),
        }}
      >
        <div
          style={{
            width: pickByViewport(ui.isMobile, "", "280px"),
            marginRight: pickByViewport(ui.isMobile, "8px", "32px"),
          }}
        >
          <CommonSearchBar
            onSearch={filters.handleSearch}
            isReset={filters.isResetSearch}
            ref={filters.searchRef}
            value={filters.searchText}
            placeholder="Search members by ID, name or programs..."
          />
        </div>
        <Button
          variant="outlined"
          startIcon={
            filters.activeFilterCount > 0 && (
              <Box
                sx={{
                  backgroundColor: "#403C6F",
                  color: "white",
                  fontSize: "9px",
                  fontWeight: 400,
                  height: "16px",
                  minWidth: "16px",
                  borderRadius:
                    filters.activeFilterDigits > 1 ? "999px" : "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  padding: filters.activeFilterDigits > 1 ? "0 6px" : 0,
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",

                  "& > span": {
                    position: "relative",
                    top: "0.5px",
                    fontSize: "12px",
                    lineHeight: 1,
                  },
                }}
              >
                <span>{filters.activeFilterCount}</span>
              </Box>
            )
          }
          endIcon={<FilterListRoundedIcon style={{ fontSize: "18px" }} />}
          onClick={() => ui.setFiltersVisible(!ui.filtersVisible)}
          sx={{
            height: "35px",
            minWidth: "90px",
            borderRadius: "48px",
            border: "1px solid #E8E8E9",
            padding: "6px 12px 6px 16px",
            textTransform: "none",
            color: "#403C6F",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "0.1px",
            gap: "4px",
            backgroundColor: (() => {
              if (filters.activeFilterCount > 0) return "#FAFAFF";
              if (ui.filtersVisible) return "#ECECF1";
              return "#FFFFFF";
            })(),
            "& .MuiButton-endIcon": {
              marginLeft: "4px",
              marginRight: "-4px",
            },
            "&:hover": {
              borderColor: "#403C6F",
              backgroundColor: ui.filtersVisible
                ? "#ECECF1"
                : "rgba(64, 60, 111, 0.04)",
            },
          }}
        >
          Filter
        </Button>
        {filters.activeFilterCount > 0 &&
          (ui.isMobile ? (
            <Box
              onClick={filters.handleClearFilters}
              sx={{
                height: "35px",
                width: "35px",
                minWidth: "35px",
                borderRadius: "50%",
                border: "1px solid #E8E8E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#FFFFFF",
                color: "#403C6F",
                "&:hover": {
                  backgroundColor: "#F5F5F5",
                  borderColor: "#403C6F",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </Box>
          ) : (
            <Button
              variant="outlined"
              onClick={filters.handleClearFilters}
              sx={{
                height: "33px",
                minWidth: "70px",
                borderRadius: "48px",
                border: "1px solid #66638C",
                padding: "6px 10px",
                textTransform: "none",
                color: "#403C6F",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                letterSpacing: "0.1px",
                backgroundColor: "#FFFFFF",
                marginRight: "0px",
                marginLeft: "0px",
                "&:hover": {
                  borderColor: "#66638C",
                  backgroundColor: "#F7F8FB",
                },
              }}
            >
              Clear Filter
            </Button>
          ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          width: pickByViewport(ui.isMobile, "100%", "auto"),
          justifyContent: pickByViewport(ui.isMobile, "flex-start", "flex-end"),
        }}
      >
        {showBulkAssignee && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#403C6F",
                whiteSpace: "nowrap",
              }}
            >
              Assign To:
            </Typography>
            <Box
              sx={{
                width: "fit-content",
                minWidth: "fit-content",
                maxWidth: "fit-content",
                "& .common-filter-input": {
                  flex: "0 0 auto",
                  minWidth: "unset",
                  maxWidth: "none",
                },
                "& .filter-input-containers": {
                  width: "170px",
                },
                "& .filter-input": {
                  width: "170px",
                },
              }}
            >
              <CommonSingleSelect
                type="radio-select"
                label="Assignee"
                showLabel={false}
                options={bulkAssigneeOptions}
                value={bulkAssignee}
                onChange={onBulkAssigneeChange}
                filterType="ASSIGNEE"
                instanceId="ASSIGNEE_BULK_MEMBER_OUTREACH"
              />
            </Box>
          </div>
        )}

        {role === "L9" && (
          <>
            <Button
              variant="outlined"
              onClick={() => setIsAddPatientOpen(true)}
              startIcon={<PlusIcon size={16} />}
              sx={{
                height: "35px",
                minWidth: "120px",
                borderRadius: "48px",
                border: "1px solid #E8E8E9",
                padding: "6px 16px",
                textTransform: "none",
                color: "#403C6F",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                letterSpacing: "0.1px",
                backgroundColor: "#FFFFFF",
                width: ui.isMobile ? "100%" : "auto",
                "&:hover": {
                  borderColor: "#403C6F",
                  backgroundColor: "rgba(64, 60, 111, 0.04)",
                },
              }}
            >
              Add Patient
            </Button>

            <AddPatientModal
              open={isAddPatientOpen}
              onClose={() => setIsAddPatientOpen(false)}
              onAdd={handleAddPatients}
            />
          </>
        )}

        <Button
          variant="outlined"
          onClick={handleExportExcel}
          disabled={exportLoading || !totalRecords || totalRecords === 0}
          startIcon={<DownloadIcon size={16} />}
          sx={{
            height: "35px",
            minWidth: "120px",
            borderRadius: "48px",
            border: "1px solid #E8E8E9",
            padding: "6px 16px",
            textTransform: "none",
            color: "#403C6F",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "0.1px",
            backgroundColor: "#FFFFFF",
            width: ui.isMobile ? "100%" : "auto",
            "&:hover": {
              borderColor: "#403C6F",
              backgroundColor: "rgba(64, 60, 111, 0.04)",
            },
            "&:disabled": {
              borderColor: "#E8E8E9",
              color: "#A0A0A0",
            },
          }}
        >
          {exportLoading ? "Exporting..." : "Export"}
        </Button>

        {isPriorityOutreach && isL6L7 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              bgcolor: "#fff",
              color: "text.primary",
              textTransform: "none",
              padding: 0,
              minWidth: 0,
              "&:hover": { bgcolor: "#fff" },
              height: 32,
              borderRadius: 20,
              px: 1,
            }}
          >
            <Typography sx={getClaimTypeLabelSx(claimType === "PAID")}>
              Paid claims
            </Typography>
            <ToggleButton
              label=""
              onLabel=""
              offLabel=""
              isOn={claimType === "REJECTED"}
              onChange={() => {
                onClaimTypeChange(claimType === "PAID" ? "REJECTED" : "PAID");
                filters.handleClearFilters(
                  claimType === "PAID" ? "REJECTED" : "PAID",
                ); // Clear all other filters and set the new claim type
              }}
            />
            <Typography sx={getClaimTypeLabelSx(claimType === "REJECTED")}>
              Rejected claims
            </Typography>
          </Box>
        )}
      </div>
    </div>
  );
};

MembersFilterBar.propTypes = {
  ui: PropTypes.object,
  filters: PropTypes.object,
  claimType: PropTypes.string,
  onClaimTypeChange: PropTypes.func,
  isPriorityOutreach: PropTypes.bool,
  showBulkAssignee: PropTypes.bool,
  bulkAssignee: PropTypes.string,
  onBulkAssigneeChange: PropTypes.func,
  bulkAssigneeOptions: PropTypes.arrayOf(PropTypes.string),
  totalRecords: PropTypes.number,
  onRefresh: PropTypes.func,
};

const MembersFilters = ({ ui, filters }) => {
  const commonProps = {
    programs: Array.isArray(filters.programs) ? filters.programs : [],
    carriers: Array.isArray(filters.carrier) ? filters.carrier : [],
    groups: Array.isArray(filters.groups) ? filters.groups : [],
    assignees: Array.isArray(filters.assignees) ? filters.assignees : [],
    programFilter: filters.programFilter,
    carrierFilter: filters.carrierFilter,
    groupFilter: filters.groupFilter,
    assigneeFilter: filters.assigneeFilter,
    dispositionFilter: filters.dispositionFilter,
    onProgramChange: filters.handleProgramChange,
    onCarrierChange: filters.handleCarrierChange,
    onGroupChange: filters.handleGroupChange,
    onDispositionChange: filters.handleDispositionChange,
    onAssigneeChange: filters.handleAssigneeChange,
    onSearch: filters.onDropdownSearch,
    onOpenDropdown: filters.handleDropdownOpen,
    onLoadMoreOptions: filters.handleDropdownLoadMore,
    loadingMap: filters.dropdownLoading,
    hasMoreMap: filters.dropdownHasMore,
    visibleFilters: [
      "STATUS",
      "CARRIER",
      "GROUP",
      "PROGRAM",
      "ASSIGNEE",
      "RECENT_ACTIVITY",
    ],
    programSearch: filters.programFilterSearch,
    carrierSearch: filters.carrierFilterSearch,
    groupSearch: filters.groupFilterSearch,
    assigneeSearch: filters.assigneeFilterSearch,
    recentActivityOutcomes: filters.recentActivityOutcomeNames,
    recentActivityNestedOptions: filters.recentActivityNestedOptions,
    recentActivityOutcomeFilter: filters.recentActivityOutcomeFilter,
    recentActivityReasonFilter: filters.recentActivityReasonFilter,
    onRecentActivityChange: filters.handleRecentActivityChange,
    autoPrefetch: false,
    compact: ui.isMobile,
  };

  const FilterComponent = ui.isMobile ? MobileCommonFilters : CommonFilters;
  const filterKey = `${ui.isMobile ? "mobile" : "desktop"}-filters-${
    filters.filterResetKey
  }`;

  return (
    <div className="search-filters-row">
      <MobileFilterWrapper>
        <FilterComponent {...commonProps} key={filterKey} />
      </MobileFilterWrapper>
    </div>
  );
};

MembersFilters.propTypes = {
  ui: PropTypes.object,
  filters: PropTypes.object,
};

// Selecting a primary member for bulk assignment also covers the family
// members nested under them (assignment is family-wide) - returns each selected
// row's id plus the member ids of its family sub-rows.
// Bulk assignment is capped at this many primary (top-level) members.
const MAX_BULK_SELECTION = 200;

const getRowMemberId = (row) =>
  String(row?.member?.memberId ?? row?.memberId ?? row?.id ?? "");

const expandSelectionWithFamily = (selectedIds, content) => {
  const selected = new Set((selectedIds || []).map(String));
  const expanded = new Set(selected);
  (content || []).forEach((row) => {
    if (!selected.has(getRowMemberId(row))) return;
    (row.subRows || []).forEach((sub) => {
      const id = getRowMemberId(sub);
      if (id) expanded.add(id);
    });
  });
  return [...expanded];
};

// The "selected for bulk assignment" count follows the same rule as Total
// Records: members with no program/opportunity aren't counted (a programless
// primary can be selected - it's only shown for the family under it - but
// it's not one of the records in the total).
const countSelectedMembers = (selectedIds, content) => {
  const selected = new Set((selectedIds || []).map(String));
  const hasPrograms = (row) =>
    Array.isArray(row?.programs) && row.programs.length > 0;
  let count = 0;
  (content || []).forEach((row) => {
    if (!selected.has(getRowMemberId(row))) return;
    if (hasPrograms(row)) count += 1;
    count += (row.subRows || []).filter(hasPrograms).length;
  });
  return count;
};

const MembersResults = ({
  data,
  ui,
  tableRef,
  columns,
  enableRowSelection,
  onRowSelectionChange,
  selectedRows,
}) => {
  const selectionLimitReached =
    enableRowSelection && (selectedRows?.length || 0) >= MAX_BULK_SELECTION;
  // Family members are nested under their primary as collapsible sub-rows
  // (see transformContent in api/priority.js) - only claim the count includes
  // them when at least one row actually has some.
  const showsFamilyColumn = (data.results.content || []).some(
    (item) => Array.isArray(item.subRows) && item.subRows.length > 0,
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent:
            enableRowSelection && selectedRows && selectedRows.length > 0
              ? "space-between"
              : "flex-end",
          alignItems: "center",
          pr: 2,
          mt: 1,
        }}
      >
        {enableRowSelection && selectedRows && selectedRows.length > 0 && (
          <Typography
            sx={{
              color: "#403C6F",
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "150%",
              letterSpacing: 0,
              ml: 2,
              userSelect: "none",
            }}
          >
            {(() => {
              const selectedCount = countSelectedMembers(
                selectedRows,
                data.results.content,
              );
              return `${selectedCount} ${
                selectedCount === 1 ? "member" : "members"
              } selected for bulk assignment`;
            })()}
          </Typography>
        )}
        {selectionLimitReached && (
          <Typography
            role="alert"
            sx={{
              color: "#B54708",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "150%",
              ml: 2,
              mr: "auto",
              userSelect: "none",
            }}
          >
            {`Only ${MAX_BULK_SELECTION} rows can be selected at a time for bulk assignment. Deselect a row to select another.`}
          </Typography>
        )}
        <Typography
          sx={{
            color: "#080A0E8A",
            fontWeight: 500,
            fontStyle: "italic",
            fontSize: "16px",
            lineHeight: "150%",
            letterSpacing: 0,
            ml: 2,
            userSelect: "none",
          }}
        >
          {`Total ${data.results.totalElements} ${
            data.results.totalElements === 1 ? "Record" : "Records"
          }${showsFamilyColumn ? " ( Including all family members)" : ""}`}
        </Typography>
      </Box>

      <div className="search-results-container" ref={ui.resultsContainerRef}>
        {data.error && <p style={{ color: "red" }}>{data.error}</p>}
        {!data.error && (
          <div style={{ marginBottom: "16px" }}>
            <CommonTable
              ref={tableRef}
              columns={columns}
              data={data.results.content || []}
              enableRowSelection={enableRowSelection}
              onRowSelectionChange={onRowSelectionChange}
              selectedRows={selectedRows}
              paginate={false}
              getSubRows={(row) => row.subRows}
              manualSorting
              sorting={data.sorting}
              onSortingChange={data.setSorting}
              getRowClassName={(row) => {
                const rowId = row.memberId || row.id;
                return rowId === ui.quickViewMember ? "row-highlight" : "";
              }}
              filtersVisible={ui.filtersVisible}
              loading={data.loading}
              pageSize={200}
              tableHeight={ui.computedTableHeight}
              virtualize={true}
              isItemLoaded={data.isItemLoaded}
              itemCount={data.totalRows}
              onLoadMoreItems={data.loadMoreItems}
            />
          </div>
        )}
      </div>
    </>
  );
};

MembersResults.propTypes = {
  data: PropTypes.object,
  ui: PropTypes.object,
  tableRef: PropTypes.object,
  columns: PropTypes.array,
  enableRowSelection: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  selectedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};

const MembersPage = (props) => {
  const location = useLocation();
  const { showLoading, hideLoading } = useLoading();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const role = userDetails?.primaryRoleName;
  const isPhiToggleOn = useSelector((state) => state.toggles.phi);
  const isShowPhiEnabled = !isPhiToggleOn;
  const dispatch = useDispatch();
  const tableRef = useRef(null);

  const isMemberMonitoring = location.pathname.includes("monitoring");
  const isPriorityOutreach =
    props.isPriorityOutreach === undefined
      ? location.pathname.includes("member-outreach")
      : props.isPriorityOutreach;

  const getCurrentSection = () => {
    if (isPriorityOutreach) return "member-outreach";
    if (isMemberMonitoring) return "monitoring";
    return "members";
  };
  const [activeSection] = useState(getCurrentSection());
  const currentSection = activeSection ? SECTIONS[activeSection] : null;
  const sectionTitle = currentSection?.title || "";
  const sectionDescription = currentSection?.description || "";

  const ui = useMembersUI(isPriorityOutreach, isMemberMonitoring);
  const filters = useMembersFilter(isPriorityOutreach, isMemberMonitoring);
  const [assigneeVisitKey] = useState(
    () =>
      `${Date.now()}-${performance.now().toString(36).replace(".", "")}-${Math.floor(Date.now() / 1000)}`,
  );

  const navigationStateAppliedRef = useRef(false);
  const [pendingAssignee, setPendingAssignee] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState("Unassigned");
  const bulkAssignInFlightRef = useRef(false);
  const lastBulkAssignSignatureRef = useRef("");
  const bulkAssigneeOptions = useMemo(() => {
    const names = Array.isArray(filters.assignees)
      ? filters.assignees.filter((name) => Boolean(name) && name !== "N/A")
      : [];
    return Array.from(new Set(["Unassigned", ...names]));
  }, [filters.assignees]);

  const showBulkAssignee = isPriorityOutreach && selectedRowIds.length > 0;
  const assigneeOptionsCount = Array.isArray(filters.assignees)
    ? filters.assignees.length
    : 0;
  const isAssigneeDropdownLoading = !!filters.dropdownLoading?.ASSIGNEE;
  const fetchAssigneeOptions = filters.fetchOptionsForType;

  const shouldSuppressFetch = !!(
    pendingAssignee ||
    ((location.state?.assignee || location.state?.assigneeId) &&
      isPriorityOutreach &&
      !navigationStateAppliedRef.current)
  );
  if (shouldSuppressFetch && filters.suppressNextTableFetchRef) {
    filters.suppressNextTableFetchRef.current = true;
  }

  useEffect(() => {
    const { assignee, assigneeId } = location.state || {};
    if (
      (assignee || assigneeId) &&
      isPriorityOutreach &&
      !navigationStateAppliedRef.current
    ) {
      navigationStateAppliedRef.current = true;

      dispatch(resetMembersFilters());

      if (assigneeId != null) {
        dispatch(
          setMembersFilters({
            assigneeFilter: assignee ? [assignee] : [],
            selectedAssigneeIds: [assigneeId],
          }),
        );
        filters.assigneeSelectionTriggerRef.current = true;
        filters.suppressNextTableFetchRef.current = false;

        if (filters.assignees.length === 0) {
          filters.fetchOptionsForType("ASSIGNEE", "", "", true);
        }
      } else if (assignee) {
        setPendingAssignee(assignee);
      }

      globalThis.history.replaceState({}, document.title);
    }
  }, [location.state, isPriorityOutreach, dispatch, filters]);

  useEffect(() => {
    if (pendingAssignee) {
      if (filters.assignees.length > 0) {
        filters.handleAssigneeChange([pendingAssignee]);
        setPendingAssignee(null);
      } else if (!filters.dropdownLoading.ASSIGNEE) {
        filters.fetchOptionsForType("ASSIGNEE", "", "", true);
      }
    }
  }, [
    pendingAssignee,
    filters.assignees,
    filters.dropdownLoading.ASSIGNEE,
    filters.handleAssigneeChange,
    filters.fetchOptionsForType,
    filters,
  ]);

  useEffect(() => {
    if (!showBulkAssignee) {
      setBulkAssignee("Unassigned");
    }
  }, [showBulkAssignee]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters.claimType, isPriorityOutreach]);

  useEffect(() => {
    if (!showBulkAssignee) return;
    if (assigneeOptionsCount > 0) return;
    if (isAssigneeDropdownLoading) return;

    fetchAssigneeOptions("ASSIGNEE", "", "", true);
  }, [
    showBulkAssignee,
    assigneeOptionsCount,
    isAssigneeDropdownLoading,
    fetchAssigneeOptions,
  ]);

  useEffect(() => {
    if (!bulkAssigneeOptions.includes(bulkAssignee)) {
      setBulkAssignee("Unassigned");
    }
  }, [bulkAssignee, bulkAssigneeOptions]);

  const data = useMembersData(
    {
      isPriorityOutreach,
      isMemberMonitoring,
      selectedProgramIds: filters.selectedProgramIds,
      selectedAssigneeIds: filters.selectedAssigneeIds,
      selectedGroupIds: filters.selectedGroupIds,
      selectedCarrierIds: filters.selectedCarrierIds,
      selectedDispositionIds: filters.selectedDispositionIds,
      searchText: filters.searchText,
      claimType: filters.claimType,
      pageSize: 200,
    },
    {
      suppressNextTableFetchRef: filters.suppressNextTableFetchRef,
      carrierSelectionIsManualRef: filters.carrierSelectionIsManualRef,
      programSelectionTriggerRef: filters.programSelectionTriggerRef,
      assigneeSelectionTriggerRef: filters.assigneeSelectionTriggerRef,
      dispositionSelectionTriggerRef: filters.dispositionSelectionTriggerRef,
      showLoading,
      hideLoading,
    },
  );

  // "Select all" first loads every remaining page, then selects every loaded
  // top-level row - so the selection (and the bulk-assignment count) always
  // covers the whole result set, not just whatever's been scrolled into view.
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const handleToggleAll = useCallback(
    (checked) => {
      if (!checked || selectedRowIds.length > 0) {
        setIsSelectingAll(false);
        setSelectedRowIds([]);
        return;
      }
      setIsSelectingAll(true);
    },
    [selectedRowIds.length],
  );

  const loadAllRemainingPagesRef = useRef(data.loadAllRemainingPages);
  loadAllRemainingPagesRef.current = data.loadAllRemainingPages;
  const loadedContentRef = useRef(data.results?.content);
  loadedContentRef.current = data.results?.content;

  useEffect(() => {
    if (!isSelectingAll) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        // Only MAX_BULK_SELECTION rows can be selected, so there is no need to
        // load every page when enough rows are already loaded.
        const loaded = loadedContentRef.current || [];
        const content =
          loaded.length >= MAX_BULK_SELECTION
            ? loaded
            : await loadAllRemainingPagesRef.current();
        if (cancelled) return;
        // Same id the table itself uses to identify a row for selection -
        // rows with an empty member id are still part of the selection (the
        // table matches them by that empty id), so none are dropped here.
        const ids = (content || []).map((row, index) =>
          String(
            row?.id ??
              row?.memberId ??
              row?.member?.memberId ??
              row?.manualTask?.id ??
              index,
          ),
        );
        setSelectedRowIds(ids.slice(0, MAX_BULK_SELECTION));
      } catch (error) {
        console.error("Select all failed to load every page:", error);
      } finally {
        if (!cancelled) setIsSelectingAll(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSelectingAll]);

  useEffect(() => {
    if (!isSelectingAll) return undefined;
    showLoading();
    return () => hideLoading();
  }, [isSelectingAll, showLoading, hideLoading]);

  const handleBulkAssigneeChange = useCallback(
    async (selectedName) => {
      setBulkAssignee(selectedName || "Unassigned");

      const memberIds = expandSelectionWithFamily(
        Array.isArray(selectedRowIds) ? selectedRowIds : [],
        data.results?.content,
      )
        .map((id) => +id)
        // An empty/non-numeric id isn't a real member - never send 0.
        .filter((id) => Number.isFinite(id) && id > 0);

      if (memberIds.length === 0) return;

      const assigneeId =
        selectedName === "Unassigned"
          ? 0
          : filters.assigneeNameToIdMap?.[selectedName];

      if (assigneeId === undefined || assigneeId === null) {
        console.warn(
          "Selected assignee ID not found for bulk assignment:",
          selectedName,
        );
        return;
      }

      const normalizedMemberIds = [...memberIds].sort((a, b) => a - b);
      const requestSignature = `${assigneeId}:${normalizedMemberIds.join(",")}`;

      if (bulkAssignInFlightRef.current) {
        return;
      }
      if (lastBulkAssignSignatureRef.current === requestSignature) {
        return;
      }

      try {
        bulkAssignInFlightRef.current = true;
        lastBulkAssignSignatureRef.current = requestSignature;
        showLoading();
        await updateBulkMemberAssignee(assigneeId, memberIds);
        setSelectedRowIds([]);
        if (tableRef.current?.scrollToTop) {
          tableRef.current.scrollToTop();
        } else if (tableRef.current?.scrollElement) {
          tableRef.current.scrollElement.scrollTop = 0;
        }
        await data.refreshData();
      } catch (error) {
        console.error("Bulk assignee update failed:", error);
        // allow retry with same payload after a failed attempt
        lastBulkAssignSignatureRef.current = "";
      } finally {
        bulkAssignInFlightRef.current = false;
        hideLoading();
      }
    },
    [
      selectedRowIds,
      filters.assigneeNameToIdMap,
      data,
      showLoading,
      hideLoading,
    ],
  );

  // The leading expand/collapse column only makes sense when some row has
  // family sub-rows - otherwise it would just be an empty column.
  const hasFamilySubRows = (data.results?.content || []).some(
    (item) => Array.isArray(item.subRows) && item.subRows.length > 0,
  );

  const columns = React.useMemo(() => {
    const isL6L7 = role === "L6" || role === "L7";
    const isRejectedClaims = filters.claimType === "REJECTED" && isL6L7;
    const baseColumns = getColumns(
      ui.handleQuickView,
      assigneeVisitKey,
      true,
      isShowPhiEnabled,
      isRejectedClaims,
      selectedRowIds.length > 0,
    ).filter(
      (col) =>
        (hasFamilySubRows || col.id !== "expander") &&
        (!isRejectedClaims || col.id !== "priorityLevel"),
    );
    if (!isPriorityOutreach || !["L6", "L7", "L8", "L9"].includes(role)) {
      return baseColumns;
    }
    return [
      {
        ...checkboxColumn,
        meta: {
          onToggleAll: handleToggleAll,
          isSelectingAll,
          selectionLimitReached: selectedRowIds.length >= MAX_BULK_SELECTION,
        },
      },
      ...baseColumns,
    ];
  }, [
    ui.handleQuickView,
    isShowPhiEnabled,
    assigneeVisitKey,
    isPriorityOutreach,
    filters.claimType,
    selectedRowIds,
    role,
    hasFamilySubRows,
    handleToggleAll,
    isSelectingAll,
  ]);

  const quickViewMemberProps = React.useMemo(() => {
    if (!ui.quickViewMember || !ui.selectedMember) return null;

    return {
      onClose: ui.handleCloseQuickView,
      source: isPriorityOutreach ? "priorityOutreach" : "memberMonitoring",
      memberData: {
        ...ui.selectedMember,
        name: ui.selectedMember.member?.name,
        memberId: ui.selectedMember.member?.memberId,
        personCode: `${ui.selectedMember.member?.memberId}-Subscriber/Policy Holder`,
        status: ui.selectedMember.recentActivity?.status || "",
        gender: ui.selectedMember.member?.gender || "",
        dateOfBirth: ui.selectedMember.member?.dob || "",
        allergies: ui.selectedMember.member?.knownAllergies || ["None"],
        contact: {
          phone: ui.selectedMember.provider?.contact || "",
          email:
            ui.selectedMember.member?.email ||
            `${
              ui.selectedMember.member?.name
                ?.toLowerCase()
                .replaceAll(/\s+/g, ".") || ""
            }N/A`,
          language: ui.selectedMember.member?.language || "",
          timeZone: ui.selectedMember.member?.timezone || "",
          address: ui.selectedMember.member?.address || "",
        },
        notes: Array.isArray(ui.selectedMember.notes)
          ? ui.selectedMember.notes
          : [],
      },
      onNoteUpdate: (updatedNotes) => {
        if (ui.selectedMember?.member?.memberId) {
          localStorage.setItem(
            `member_${ui.selectedMember?.member?.memberId}_notes`,
            JSON.stringify(updatedNotes),
          );
        }
      },
    };
  }, [
    ui.quickViewMember,
    ui.selectedMember,
    isPriorityOutreach,
    ui.handleCloseQuickView,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "transparent",
        overflow: "hidden !important",
      }}
    >
      <CssBaseline />

      <MainContent open={false} className="search-page">
        <MembersPageHiddenTitle
          isPriorityOutreach={isPriorityOutreach}
          isMemberMonitoring={isMemberMonitoring}
        />
        <div className="search-page-container">
          <div
            className="search-page-content"
            style={{
              transform: `scale(${ui.containerScale})`,
              transformOrigin: "top left",
              width: ui.containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <MembersPageVisibleHeader
              isPriorityOutreach={isPriorityOutreach}
              isMemberMonitoring={isMemberMonitoring}
              isPhiToggleOn={isPhiToggleOn}
              onPhiToggle={() => dispatch(togglePhi())}
              sectionTitle={sectionTitle}
              sectionDescription={sectionDescription}
            />
            <MembersFilterBar
              ui={ui}
              filters={filters}
              claimType={filters.claimType}
              onClaimTypeChange={(val) =>
                dispatch(setMembersFilters({ claimType: val }))
              }
              isPriorityOutreach={isPriorityOutreach}
              isMemberMonitoring={isMemberMonitoring}
              showBulkAssignee={showBulkAssignee}
              bulkAssignee={bulkAssignee}
              onBulkAssigneeChange={handleBulkAssigneeChange}
              bulkAssigneeOptions={bulkAssigneeOptions}
              totalRecords={data?.results?.totalElements || 0}
              onRefresh={data?.refreshData}
            />

            {ui.filtersVisible && <MembersFilters ui={ui} filters={filters} />}

            <MembersResults
              data={data}
              ui={ui}
              tableRef={tableRef}
              columns={columns}
              enableRowSelection={
                isPriorityOutreach && ["L6", "L7", "L8", "L9"].includes(role)
              }
              onRowSelectionChange={setSelectedRowIds}
              selectedRows={selectedRowIds}
            />
          </div>
        </div>
      </MainContent>

      {quickViewMemberProps && <MemberQuickview {...quickViewMemberProps} />}
    </Box>
  );
};

MembersPage.propTypes = {
  isPriorityOutreach: PropTypes.bool,
};

export default MembersPage;
